import { Icon } from "@/components/atoms/Icon";
import { getGridCellBorderEdges } from "@/components/organisms/Table/Table.tools";
import {
  useTreeStore,
  useTreeStoreApi,
  calculateVisibleIds,
} from "@/components/organisms/Tree/Tree.store";
import { Values } from "@/constants";
import { CellProps } from "@/hooks/useContextTree";
import React, { useRef } from "react";

import { icons } from "@/objects/@icons";
import { Button } from "@/components/ui/button";
import { cn } from "@/components/ui/utils";

// Utility functions for summary nodes
const isSummaryNode = (id: string) => id?.endsWith("-summary") || false;

interface TreeChevronCellProps {
  nodeId: string;
  rowIndex: number;
  isSticky?: boolean;
  isLoading?: boolean;
  isLastRow?: boolean;
  cellProps?: CellProps;
  contextId?: string;
}

export function TreeChevronCellInner({
  nodeId,
  rowIndex,
  isSticky = false,
  isLoading = false,
  isLastRow = false,
  cellProps,
  contextId: passedContextId,
}: TreeChevronCellProps) {
  const storeApi = useTreeStoreApi();
  const navRef = useRef<HTMLDivElement>(null);
  const storeContextId = useTreeStore((state) => state.contextId);
  const contextId = passedContextId || storeContextId;
  const hideOuterBorders = useTreeStore(
    (state) => state.layout?.hideOuterBorders ?? false,
  );
  const borderEdges = getGridCellBorderEdges({
    color: "var(--border)",
    isOuterBottom: isLastRow,
    hideOuterEdges: hideOuterBorders,
  });

  // Get node data from store
  const node = useTreeStore((state) => (nodeId ? state.treeMap[nodeId] : null));
  const isExpanded = useTreeStore((state) =>
    nodeId ? state.treeMap[nodeId]?.isExpanded : false,
  );
  const nodeHasChildren = useTreeStore((state) =>
    nodeId ? state.treeMap[nodeId]?.childrenIds?.length > 0 : false,
  );
  const handleBranchToggle = useTreeStore((state) =>
    isSticky ? state.handleStickyBranchToggle : state.handleBranchToggle,
  );

  // Determine if this row should show a chevron
  const isSummary = node ? isSummaryNode(node.id) : false;
  const hasChevron = nodeHasChildren && !isSummary && !isLoading;

  // Cell is navigable if cellProps is defined
  // (TreeLayout filters out duplicate rows, so no need to check isNodeInStickyRows)
  const isNavigable = cellProps !== undefined;

  // Extract navigation props from cellProps
  const { className: _cellClassName, ...cellNavProps } = cellProps || {};

  // Manage cell registration when cellProps changes
  // The ref callback only runs on mount/unmount, so we need an effect for dynamic registration
  React.useEffect(() => {
    if (!cellProps?.ref || !navRef.current) return;
    // Register the cell
    cellProps.ref(navRef.current);

    // Cleanup: unregister when unmounted or cellProps changes
    return () => {
      if (cellProps?.ref) {
        cellProps.ref(null);
      }
    };
  }, [cellProps]);

  // After toggle, refocus the chevron cell (it may have moved from sticky to regular or vice versa)
  const refocusAfterToggle = React.useCallback(() => {
    // Wait for the DOM to update after the toggle
    requestAnimationFrame(() => {
      // Find the row index in the full visible list (not the windowed visibleRowNodes)
      const state = storeApi.getState();
      const visibleIds = calculateVisibleIds(state.treeMap, state.layout);
      const actualIndex = visibleIds.indexOf(nodeId);
      if (actualIndex >= 0) {
        // Look up the cell in the registry using data attributes
        // The chevron is always at colIndex 0
        const cellSelector = `[data-row-index="${actualIndex}"][data-col-index="0"]`;
        const cell = document.querySelector(cellSelector) as HTMLElement;
        if (cell) {
          cell.focus({ preventScroll: true });
        }
      }
    });
  }, [nodeId]);

  // Handle toggle on Enter or Space
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === "Enter" || e.key === " ") && hasChevron) {
      e.preventDefault();
      e.stopPropagation();
      handleBranchToggle(nodeId, 0);
      refocusAfterToggle();
    }
  };

  // Handle click toggle
  const handleClick = (e: React.MouseEvent) => {
    if (hasChevron) {
      e.stopPropagation();
      handleBranchToggle(nodeId, 0);
      refocusAfterToggle();
    }
  };

  // Calculate chevron width - same as row height for a square cell
  const chevronWidth = Values.DEFAULT_ROW_HEIGHT;

  return (
    <div
      className={cn(
        `${contextId}-row-${rowIndex}`,
        "absolute top-0 left-0 flex items-center justify-center bg-(--grid-row-bg) box-border",
      )}
      data-grid-cell
      style={{
        width: chevronWidth,
        height: Values.DEFAULT_ROW_HEIGHT,
        ...borderEdges,
      }}
    >
      {isNavigable ? (
        <div
          {...cellNavProps}
          ref={(el: HTMLDivElement | null) => {
            navRef.current = el;
            const navProps = cellNavProps as CellProps;
            if (navProps?.ref) {
              navProps.ref(el);
            }
          }}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          onClick={handleClick}
          className={cn(
            (cellNavProps as CellProps)?.className,
            "w-full h-full flex items-center justify-center [outline:none] relative [transition:box-shadow_0.15s_ease-in-out]",
            hasChevron ? "cursor-pointer" : "cursor-default",
            // Focused, an edge in the neutral colour drawn over the cell.
            "focus:after:content-[''] focus:after:absolute focus:after:top-0 focus:after:left-0 focus:after:right-0 focus:after:bottom-0 focus:after:[border:1px_solid_var(--muted-foreground)] focus:after:pointer-events-none",
          )}
        >
          {hasChevron && (
            <Icon
              ReactIcon={icons.chevronRight}
              style={{
                transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
                color: "var(--muted-foreground)",
              }}
            />
          )}
        </div>
      ) : (
        // Non-navigable version (for rows without cell registration)
        hasChevron && (
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={handleClick}
            className="cursor-pointer p-0.5 focus:[box-shadow:inset_0_0_0_2px_color-mix(in_srgb,var(--primary)_50%,transparent_50%)]"
          >
            <Icon
              ReactIcon={icons.chevronRight}
              style={{
                transform: isExpanded ? "rotate(45deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
            />
          </Button>
        )
      )}
    </div>
  );
}

export const TreeChevronCell = React.memo(
  TreeChevronCellInner,
  (prevProps, nextProps) => {
    return (
      prevProps.nodeId === nextProps.nodeId &&
      prevProps.rowIndex === nextProps.rowIndex &&
      prevProps.isSticky === nextProps.isSticky &&
      prevProps.isLoading === nextProps.isLoading &&
      prevProps.isLastRow === nextProps.isLastRow &&
      prevProps.contextId === nextProps.contextId &&
      // Check if cellProps presence changed (for registration)
      (prevProps.cellProps !== undefined) ===
        (nextProps.cellProps !== undefined) &&
      // Check if data-row-index changed (for correct registration key)
      prevProps.cellProps?.["data-row-index"] ===
        nextProps.cellProps?.["data-row-index"]
    );
  },
);
